'use client';

import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSyncCodebaseRepos } from '@/hooks/useCodebaseRepos';
import { useUserLogged } from '@/hooks/useUserLogged';

interface SyncReposButtonProps {
  orgUUID: string;
  userUUID?: string;
  /**
   * 'labeled': full "Sync Repositories" button (management surfaces).
   * 'icon': compact "Sync" label beside Add Repo (Code Management toolbar).
   * 'iconOnly': ghost icon for dense bars (onboarding org card).
   */
  variant?: 'labeled' | 'icon' | 'iconOnly';
  /** Layout-only className — never colors/borders. */
  className?: string;
  /** Called with the terminal sync result (after queries invalidated). */
  onSynced?: (added: number) => void;
}

/**
 * Manual provider→DB repo sync trigger with live progress.
 *
 * Repo listings read the DB only; this button is how newly created provider
 * repos become visible. Streams live counts via SSE while syncing and
 * invalidates all repo-listing queries on completion.
 *
 * Renders nothing for GitHub. The sync existed to discover repos the PR bot was
 * not installed on, which needed a crawl of the provider listing. GitHub repo
 * data now comes from the App installation, so the backend does not crawl
 * GitHub at all and a manual press can only report "everything is up to date".
 * The post-install reconcile still runs on its own (`?repo_sync=1`) and
 * OnboardingRepoSyncGate still syncs automatically, so nothing depends on the
 * button. GitLab, Bitbucket and self-hosted providers keep it: they have no App
 * equivalent, so the crawl is still their only discovery path.
 */
export default function SyncReposButton({
  orgUUID,
  userUUID = '',
  variant = 'labeled',
  className,
  onSynced,
}: SyncReposButtonProps) {
  const { sync, isSyncing, liveCount } = useSyncCodebaseRepos();
  const { user: loggedUser, organization: loggedOrg } = useUserLogged();

  // No `?? 'github'` default here, unlike the provider reads on the settings
  // tabs: an org whose Source has not loaded must keep the button rather than
  // lose its only discovery path. Only a positively identified GitHub org hides
  // it. Self-hosted orgs carry a custom provider name, so they keep it too.
  const provider = (loggedOrg?.Source ?? loggedUser?.provider ?? '')
    .toString()
    .toLowerCase();

  if (provider === 'github') return null;

  const handleClick = async () => {
    const result = await sync(orgUUID, userUUID);
    if (result && result.status === 'done') onSynced?.(result.added);
  };

  const tooltip = isSyncing
    ? liveCount != null
      ? `Syncing… ${liveCount} repositories`
      : 'Syncing…'
    : 'Sync repositories';

  if (variant === 'iconOnly') {
    return (
      <TooltipProvider delay={300}>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClick}
                disabled={isSyncing || !orgUUID}
                aria-busy={isSyncing}
                aria-label="Sync repositories"
                className={className}
              >
                {isSyncing ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <RefreshCw />
                )}
              </Button>
            }
          />
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const label =
    variant === 'icon'
      ? isSyncing
        ? 'Syncing…'
        : 'Sync'
      : isSyncing
        ? liveCount != null
          ? `Syncing… ${liveCount} repos`
          : 'Syncing…'
        : 'Sync Repositories';

  const button = (
    <Button
      type="button"
      variant="outline"
      size="default"
      onClick={handleClick}
      disabled={isSyncing || !orgUUID}
      aria-busy={isSyncing}
      aria-label="Sync repositories"
      className={className}
    >
      {isSyncing ? <Loader2 className="animate-spin" /> : <RefreshCw />}
      {label}
    </Button>
  );

  if (variant === 'icon') {
    return (
      <TooltipProvider delay={300}>
        <Tooltip>
          <TooltipTrigger render={button} />
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}
